/**
 * La reapertura de una caja el mismo día, del lado del terminal.
 *
 * Business Central guarda una sola fila de apertura por (tienda, caja, fecha).
 * Hasta la extensión 1.0.4.112, reabrir la caja el mismo día reescribía esa fila
 * y la primera apertura desaparecía: la hora, el cajero y el estado quedaban los
 * de la segunda. Desde la .112 la reapertura se registra en la misma fila --en
 * `Hora reapertura` y `No. Reaperturas`-- y los dos casos que no se pueden
 * registrar se rechazan con código.
 *
 * Aquí vive lo que el terminal necesita saber de eso, sin tocar la base:
 * - la hora al minuto, que es la precisión con que abre el terminal;
 * - la explicación de los dos rechazos, para el cuadro de resultados;
 * - las aperturas que salen de una fila de Traer Históricos.
 *
 * Acuerdo `contrato-hallazgos-ciclo-cri-2026-09-14.md`, H6 · TPV.
 */

/**
 * La hora en `HH:mm`, o null si el texto no es una hora.
 *
 * El terminal abre con `HH:mm` (`AperturaDeCajaForm.js`) y Business Central la
 * devuelve con segundos (`19:56:00`). Comparadas a secas, serían distintas dos
 * horas que son la misma apertura.
 */
export const horaAlMinuto = (hora) => {
  const partes = String(hora ?? "")
    .trim()
    .match(/^(\d{1,2}):(\d{2})/);
  return partes ? `${partes[1].padStart(2, "0")}:${partes[2]}` : null;
};

export const EXPLICACION_DEL_RECHAZO = {
  APERTURA_DEL_DIA_ABIERTA:
    "Business Central tiene abierta otra apertura de esta caja hoy; esta se enviará cuando llegue su cierre.",
  APERTURA_CERRADA:
    "Business Central ya cerró una apertura de esta caja con esa hora; revisa si se abrió en otro equipo.",
};

/**
 * El motivo de Business Central con su explicación delante, si es uno de los dos
 * rechazos de la reapertura. Cualquier otro motivo vuelve tal cual.
 *
 * Con esto no se decide nada: los dos rechazos se reintentan, como todo lo que
 * el terminal no reconoce, y la apertura sigue pendiente.
 * `APERTURA_DEL_DIA_ABIERTA` se resuelve sola en cuanto llega el cierre de la
 * otra apertura. `APERTURA_CERRADA` se repetiría en cada corrida, pero desde un
 * solo equipo no se puede provocar: lo impide la guarda de misma hora de
 * `addAperturaDeCaja`.
 *
 * El código se busca en todo el texto y no sólo al principio: según el camino,
 * llega solo, detrás de `error:` o dentro del mensaje de un 400.
 */
export const conExplicacionDelRechazo = (motivo) => {
  const texto = String(motivo ?? "");
  const codigo = Object.keys(EXPLICACION_DEL_RECHAZO).find((clave) =>
    new RegExp(`\\b${clave}\\b`).test(texto)
  );
  return codigo ? `${EXPLICACION_DEL_RECHAZO[codigo]} ${texto.trim()}` : motivo;
};

/**
 * Las aperturas que representa una fila de `AperturaDeCaja` de Business Central,
 * con los campos que dependen de la reapertura. El resto lo pone quien llama.
 *
 * Una fila sin reapertura es una apertura, como hasta ahora. Con `NoReaperturas`
 * mayor que cero y `HoraDeReapertura`, son dos:
 * - la primera, con su hora, su cajero y su importe, y ya `Cerrado`: si no se
 *   hubiera cerrado, no se habría podido reabrir;
 * - la de la reapertura, con `HoraDeReapertura`, `CajeroReapertura`, importe 0 y
 *   el `Estado` de la fila.
 *
 * Las dos llevan el mismo `No`, igual que las dos aperturas locales de un
 * terminal que reabre.
 *
 * Sólo se recupera la última reapertura, porque la fila no guarda las
 * anteriores. Si Business Central no publica los campos (antes de la .112), la
 * fila sigue siendo una sola apertura. Una hora `00:00:00` es como BC publica
 * una hora vacía, así que tampoco cuenta como reapertura.
 */
export const aperturasDeLaFila = (fila) => {
  const primera = {
    HoraDeApertura: fila.HoraDeApertura || "00:00",
    Cajero: fila.Cajero,
    ImporteDeApertura: fila.ImporteDeApertura || 0,
    Estado: fila.Estado || "Cerrado",
  };

  const horaDeReapertura = horaAlMinuto(fila.HoraDeReapertura);
  const hayReapertura =
    Number(fila.NoReaperturas) > 0 && Boolean(horaDeReapertura) && horaDeReapertura !== "00:00";

  if (!hayReapertura) return [primera];

  return [
    { ...primera, Estado: "Cerrado" },
    {
      HoraDeApertura: fila.HoraDeReapertura,
      // Sin cajero de la reapertura, el de la fila: es mejor que dejarla sin
      // cajero, y la fila no tiene otro.
      Cajero: fila.CajeroReapertura || fila.Cajero,
      ImporteDeApertura: 0,
      Estado: fila.Estado || "Cerrado",
    },
  ];
};
